/**
 * UI primitives.
 *
 * Hand-written rather than pulled from a component library. The set is small on purpose — a
 * property manager's day is lists, numbers and one decisive action per row, so the vocabulary
 * needed is a card, a table, a badge and a button.
 */

import { Link } from "react-router-dom";
import { BrandSpinner } from "./spinner";
import { usePageTitle } from "@/lib/title";
import type { Permission } from "@rentorng/core/permissions";
import { useSession } from "@/lib/session";
import { Children, Fragment, isValidElement, useEffect, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { InfoTip } from "./info-tip";
import { IconArrowLeft, IconChevronDown } from "./icons";

export type Tone = "success" | "warn" | "danger" | "info" | "muted" | "brand";

/**
 * The six tones, as the shared design system names them.
 *
 * They were three utility classes each, spelled out in this file — which meant the console's badge
 * was a *different* set of three, and the two products' idea of "warn" drifted the moment either
 * palette was touched. The tone is now one class from
 * `@rentorng/core/design/system.css`, resolved against whichever skin the surface declares.
 * ADR-0157.
 */
const TONE_CLASSES: Record<Tone, string> = {
  success: "pill-success",
  warn: "pill-warn",
  danger: "pill-danger",
  info: "pill-info",
  muted: "pill-muted",
  brand: "pill-brand",
};

const TONE_DOT: Record<Tone, string> = {
  success: "bg-brand-500",
  warn: "bg-warn-500",
  danger: "bg-danger-500",
  info: "bg-info-600",
  muted: "bg-ink-400",
  brand: "bg-white",
};

export function Badge({ children, tone = "muted", dot }: { children: ReactNode; tone?: Tone; dot?: boolean }) {
  return (
    <span className={`pill ${TONE_CLASSES[tone]}`}>
      {dot ? <span className={`size-1.5 rounded-full ${TONE_DOT[tone]}`} /> : null}
      {children}
    </span>
  );
}

export function Card({
  children,
  className = "",
  padded = true,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return <div className={`card ${padded ? "p-5 sm:p-6" : ""} ${className}`}>{children}</div>;
}

/**
 * A card heading.
 *
 * `hint` used to print under the title on every card. Individually each one was worth saying;
 * together they turned every screen into a page of grey explanation with the data somewhere
 * inside it. The words are still available — behind the marker beside the heading — so a screen
 * shows what it holds and explains itself only when asked.
 */
export function CardTitle({ children, action, hint }: { children: ReactNode; action?: ReactNode; hint?: ReactNode }) {
  return (
    <div className="mb-4 flex items-start justify-between gap-3">
      <div className="flex min-w-0 items-center gap-2">
        <h2 className="text-base font-semibold text-ink-900">{children}</h2>
        {hint ? <InfoTip>{hint}</InfoTip> : null}
      </div>
      {action ? <div className="shrink-0 text-sm">{action}</div> : null}
    </div>
  );
}

/**
 * The top of every screen.
 *
 * Three lines, and they are not interchangeable. The **title** says where you are. The
 * **subtitle** is the live figure — how many, how much — which changes as the data does. The
 * **description** says what the screen is for and what can be done here, and never changes.
 *
 * They were conflated before: the counts sat where an explanation should be, so somebody opening
 * a screen for the first time was told "23 units" and nothing about what a unit is or what this
 * page lets them do with one.
 */
export function PageHeader({
  title,
  subtitle,
  description,
  actions,
  back,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  /** What this screen is for. One or two sentences, in the customer's own words. */
  description?: ReactNode;
  actions?: ReactNode;
  back?: { to: string; label: string };
}) {
  /*
    The heading is also the tab.

    Nearly every screen in this application already says what it is, once, here — so the title is
    taken from that rather than repeated as a second string somebody has to remember to change.
    A screen without a `PageHeader` calls `usePageTitle` itself.

    Only a plain string can be a title. Where the heading is built from elements the tab falls back
    to the product name — wrong in a small way, but never stale, which is the failure that matters.
  */
  usePageTitle(typeof title === "string" ? title : null);

  return (
    <div className="no-print mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        {/*
          A real target, not a hint.
          It was 13px grey text with an arrow glyph — the smallest thing on the page and, on a
          phone, smaller than a fingertip. It is a proper control now, with a drawn arrow and
          enough height to hit without aiming.
        */}
        {back ? (
          <Link
            to={back.to}
            className="-ml-2 mb-1 inline-flex items-center gap-1.5 rounded-[var(--radius-control)] px-2 py-1.5 text-sm font-medium text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-800"
          >
            <IconArrowLeft size={17} />
            <span className="truncate">{back.label}</span>
          </Link>
        ) : null}
        <h1 className="truncate text-2xl font-semibold tracking-tight sm:text-[28px]">{title}</h1>
        {subtitle ? <div className="mt-1.5 text-sm text-ink-500">{subtitle}</div> : null}
        {/* Not capped. A 65ch measure is right for a paragraph and wrong for a single sentence
            that has to sit above a table: it forced two lines and a chunk of vertical space on
            every screen. It wraps when the text genuinely needs it, and not before. */}
        {description ? <p className="mt-1.5 text-sm text-ink-500">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/**
 * A dashboard tile.
 *
 * The whole tile is a link into the filtered list behind the number — nothing on the dashboard
 * is a dead end, because a manager who can see an arrear but not open it has been told off,
 * not helped.
 */
/** The tinted disc behind a tile's icon. One per tone, so the colour means the same everywhere. */
const TONE_ICON: Record<Tone, string> = {
  danger: "bg-danger-100 text-danger-700",
  warn: "bg-warn-100 text-warn-700",
  success: "bg-brand-100 text-brand-700",
  brand: "bg-brand-100 text-brand-700",
  info: "bg-info-100 text-info-700",
  muted: "bg-ink-100 text-ink-600",
};

/**
 * A dashboard tile.
 *
 * The icon is the thing the eye lands on first, so it carries the tone rather than the small dot
 * that used to: red for arrears, amber for what is about to fall due, green for money in. The
 * number is set large and tabular because these four are read side by side and misaligned digits
 * are the reason a column of money is hard to compare.
 *
 * The whole tile is a link into the list behind the number — nothing on this screen is a dead end.
 */
export function StatTile({
  label,
  value,
  sub,
  tone = "muted",
  to,
  icon,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: Tone;
  to?: string;
  icon?: ReactNode;
}) {
  const body = (
    <div
      className={`card group h-full p-5 ${
        to ? "transition-colors hover:border-ink-300 hover:bg-ink-50" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="eyebrow">{label}</span>
          <div className="tabular mt-2.5 text-[28px] font-semibold leading-none tracking-tight text-ink-900">
            {value}
          </div>
        </div>
        {icon ? (
          <span className={`flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-surface)] ${TONE_ICON[tone]}`}>
            {icon}
          </span>
        ) : (
          <span className={`mt-1 size-2 shrink-0 rounded-full ${TONE_DOT[tone]}`} />
        )}
      </div>
      {sub ? <div className="mt-3 text-[13px] text-ink-500">{sub}</div> : null}
    </div>
  );
  return to ? (
    <Link to={to} className="block">
      {body}
    </Link>
  ) : (
    body
  );
}

export function EmptyState({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="px-6 py-14 text-center">
      <p className="text-base font-semibold text-ink-800">{title}</p>
      {hint ? <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-ink-500">{hint}</p> : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";

const variantClass: Record<ButtonVariant, string> = {
  primary: "btn-primary",
  secondary: "btn-secondary",
  danger: "btn-danger",
  ghost: "btn-ghost",
};

export function Button({
  children,
  variant = "primary",
  size = "md",
  className = "",
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: "sm" | "md"; children: ReactNode }) {
  return (
    <button
      {...rest}
      type={rest.type ?? "button"}
      className={`btn ${variantClass[variant]} ${size === "sm" ? "btn-sm" : ""} ${className}`}
    >
      {children}
    </button>
  );
}

/**
 * Show this only to somebody who may use it.
 *
 * The rule across the application is that what a role cannot do is not there — not there and
 * refused, not there and greyed out, simply absent. `Require` does that for a whole screen; this
 * does it for a button, a row action or a panel.
 */
export function Can({ need, children }: { need: Permission | Permission[]; children: ReactNode }) {
  const { can } = useSession();
  const needed = Array.isArray(need) ? need : [need];
  return needed.every((permission) => can(permission)) ? <>{children}</> : null;
}

export function LinkButton({
  to,
  children,
  variant = "primary",
  size = "md",
  className = "",
  need,
}: {
  to: string;
  children: ReactNode;
  variant?: ButtonVariant;
  size?: "sm" | "md";
  className?: string;
  /** The permission this button's destination needs. Without it the button is not drawn at all. */
  need?: Permission | Permission[];
}) {
  const { can } = useSession();
  if (need && !(Array.isArray(need) ? need : [need]).every((permission) => can(permission))) return null;

  return (
    <Link to={to} className={`btn ${variantClass[variant]} ${size === "sm" ? "btn-sm" : ""} ${className}`}>
      {children}
    </Link>
  );
}

export function Field({
  label,
  htmlFor,
  children,
  hint,
  error,
  required,
  group,
}: {
  label: string;
  htmlFor?: string;
  children?: ReactNode;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  /**
   * Set when the field holds several controls rather than one.
   *
   * A `<label>` with no `htmlFor` forwards every click inside it — including the gaps between
   * controls and the hint text underneath — to the first control it contains. Around one input
   * that is the point; around a row of toggles it means clicking blank space silently flips the
   * first one, which is exactly what it did to the property-type tiles.
   */
  group?: boolean;
}) {
  const Wrapper = group ? "div" : "label";
  return (
    <Wrapper className="block" htmlFor={group ? undefined : htmlFor} role={group ? "group" : undefined}>
      <span className="label">
        {label}
        {required ? <span className="text-danger-600"> *</span> : null}
      </span>
      {children}
      {hint && !error ? <span className="mt-1 block text-xs text-ink-500">{hint}</span> : null}
      {error ? <span className="mt-1 block text-xs text-danger-600">{error}</span> : null}
    </Wrapper>
  );
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`field ${props.className ?? ""}`} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`field ${props.className ?? ""}`} />;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} rows={props.rows ?? 4} className={`field ${props.className ?? ""}`} />;
}

/**
 * A tick box, drawn rather than borrowed.
 *
 * **The input is still a real `<input type="checkbox">`**, visually hidden underneath the box that
 * is drawn on top of it. That is the whole trick, and it is why this is worth doing at all: a
 * `<div role="checkbox">` has to reimplement focus, the space bar, the label association, the
 * indeterminate state and how a screen reader announces it, and it gets one of them wrong. The
 * browser's own control does all of that; only its appearance is replaced.
 *
 * The box takes the brand colour when it is on, so a screen of them reads as this product's rather
 * than as the operating system's — and, more usefully, a row of ticked boxes is legible at a glance
 * from across a desk, which a default grey tick is not.
 *
 * `indeterminate` is a property rather than an attribute, so it cannot be set in markup and has to
 * be written to the node. It is here because a "select all" that shows plain "off" while half the
 * list is on is telling the reader something untrue.
 */
export function Checkbox({
  label,
  hint,
  indeterminate,
  className = "",
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; hint?: ReactNode; indeterminate?: boolean }) {
  const box = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (box.current) box.current.indeterminate = Boolean(indeterminate);
  }, [indeterminate]);

  return (
    <label className={`group flex cursor-pointer items-start gap-2.5 text-sm ${rest.disabled ? "cursor-not-allowed opacity-60" : ""} ${className}`}>
      <span className="relative mt-0.5 grid size-[18px] shrink-0 place-items-center">
        <input
          ref={box}
          type="checkbox"
          {...rest}
          className="peer absolute inset-0 size-full cursor-pointer appearance-none rounded-[5px] border border-ink-300 bg-white transition-colors checked:border-brand-600 checked:bg-brand-600 indeterminate:border-brand-600 indeterminate:bg-brand-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 disabled:cursor-not-allowed group-hover:border-ink-400 checked:group-hover:border-brand-700 checked:group-hover:bg-brand-700"
        />
        {/* Drawn on top of the input, and untouchable, so the click always reaches the control. */}
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
          className="pointer-events-none relative size-3.5 text-white opacity-0 transition-opacity peer-checked:opacity-100 peer-indeterminate:opacity-0"
        >
          <path d="m5 12.5 4.5 4.5L19 7" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
          className="pointer-events-none absolute size-3.5 text-white opacity-0 transition-opacity peer-indeterminate:opacity-100"
        >
          <path d="M6 12h12" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" />
        </svg>
      </span>
      <span className="min-w-0">
        {label}
        {hint ? <span className="block text-xs leading-relaxed text-ink-500">{hint}</span> : null}
      </span>
    </label>
  );
}

/**
 * A switch, for a setting rather than an answer.
 *
 * A tick box is part of filling a form in; a switch is a thing being turned on. The two are not
 * interchangeable, and the difference is worth drawing: "list this publicly" and "offer card
 * payments" both change what the outside world sees the moment they are flipped, which is a
 * different weight of decision from "how many bathrooms".
 *
 * Built on a real checkbox with `role="switch"`, so it stays focusable, keyboard-operable and
 * labelled. It lives here rather than beside its first caller because there are two now, and two
 * hand-drawn switches drift.
 */
export function Switch({
  checked,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  /** What it turns on, for anyone not looking at the text beside it. */
  label: string;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className={`relative inline-flex shrink-0 items-center ${disabled ? "" : "cursor-pointer"}`}>
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        aria-label={label}
        onChange={(event) => onChange(event.target.checked)}
        className="peer sr-only"
      />
      <span
        className="h-6 w-11 rounded-full transition-colors peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand-500"
        style={{ backgroundColor: checked ? "var(--color-brand-600)" : "var(--color-ink-300)" }}
      />
      <span
        className={`pointer-events-none absolute left-0.5 size-5 rounded-full bg-white transition-transform ${
          checked ? "translate-x-5" : "translate-x-0"
        }`}
        style={{ boxShadow: "0 1px 2px rgb(15 23 32 / 0.2)" }}
      />
    </label>
  );
}

/**
 * A dialog, for the thing you have to do before you can carry on with the thing you were doing.
 *
 * **Used sparingly and only for that.** A modal that holds a whole screen's work is a screen
 * pretending to be a dialog; this one exists so somebody filling in a tenancy can add the unit or
 * the tenant that turns out not to exist yet, without losing the dates they have already typed.
 *
 * Escape closes it, the ground behind it closes it, and the panel stops clicks reaching that
 * ground. `aria-modal` and the heading id are what tell a screen reader it is a dialog and what
 * the dialog is called.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    // Nothing behind a dialog should scroll under it.
    const held = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = held;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      /* `items-start` with auto margins on the panel, rather than `items-center`.
         They look identical until the dialog is taller than the window, and then they could not be
         more different. Centring a flex item that overflows pushes the overflow out of **both**
         ends, and the part above the container's start edge cannot be scrolled to — scrolling only
         ever reveals what is below. So a tall dialog lost its heading, its first field and its close
         button, with no way to reach any of them; measured at 1440x420 the panel sat at top -6 and
         went further negative as the window shrank.

         Auto margins take precedence over `align-items`, so `my-auto` still centres the panel while
         it fits. When it does not fit there is no free space to distribute, the margins resolve to
         zero, and the panel starts at the top where the overflow is reachable. `mt-auto` alone keeps
         the bottom-sheet on a phone, and gives up the same way. */
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-900/40 p-0 sm:p-6"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
        /*
          **The panel is bounded by the screen, and its body scrolls inside it.**

          It used to be as tall as its content and left the *overlay* to scroll. That works in a
          desktop browser and fails on a phone, which is where it was reported: `inset-0` resolves
          against the **layout** viewport, and a phone's URL bar covers the bottom of that. So the
          last of the content — which is always the button that commits the form — sat underneath
          the browser's own chrome. Nothing in a headless test can see it, which is why this passed
          for three reports running.

          `100dvh` is the *dynamic* viewport height: it shrinks to what is genuinely visible. With
          the panel capped to it and the body scrolling within, content of any length is reachable,
          and the heading and the close button stay put instead of scrolling away.
        */
        className="card modal-sheet mt-auto flex max-h-[100dvh] w-full max-w-lg flex-col rounded-b-none p-5 sm:my-auto sm:max-h-[calc(100dvh-3rem)] sm:rounded-[var(--radius-surface)] sm:p-6"
      >
        <div className="mb-4 flex shrink-0 items-start justify-between gap-3">
          <h2 className="text-base font-semibold text-ink-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-1 -mt-1 grid size-8 place-items-center rounded-[var(--radius-control)] text-ink-500 hover:bg-ink-100 hover:text-ink-900"
          >
            ×
          </button>
        </div>
        {/* The scrolling region. `-mx-*`/`px-*` keeps a focus ring from being clipped at the edge
            while the padding still reads as the panel's own. */}
        <div className="-mx-5 min-h-0 flex-1 overflow-y-auto px-5 sm:-mx-6 sm:px-6">{children}</div>
      </div>
    </div>
  );
}

/**
 * A run of related fields, under a heading of their own.
 *
 * A long form written as one column of labels is read as one long list, and everything in it
 * weighs the same — the name of the flat, the number of toilets, who owns it. Broken into named
 * runs with a rule between them, the same fields become four or five short decisions, and
 * somebody who came to change the rent can find the rent without reading the rest.
 *
 * The first section in a form carries no rule above it: a line under the heading you just read is
 * a line in the wrong place.
 */
export function FormSection({
  title,
  hint,
  children,
  group,
}: {
  title: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
  /**
   * Set when the section's heading is the label for the controls inside it — a row of toggles
   * with no `<label>` of their own. It makes the section itself the labelled group, rather than
   * repeating the heading as a second label nobody needed to read twice.
   */
  group?: boolean;
}) {
  return (
    <section
      role={group ? "group" : undefined}
      aria-label={group && typeof title === "string" ? title : undefined}
      className="border-t border-ink-200 pt-6 first:border-t-0 first:pt-0"
    >
      <h3 className="text-sm font-semibold text-ink-900">{title}</h3>
      {hint ? <p className="mt-1 text-xs leading-relaxed text-ink-500">{hint}</p> : null}
      <div className="mt-4 space-y-4">{children}</div>
    </section>
  );
}

/**
 * A list. A table on a desk, a stack of cards in a hand.
 *
 * Six columns in 390px is not a small table, it is six unreadable ones — "Garden View Apartments"
 * came out one word per line with a three-letter landlord beside it. Below `sm` each row is drawn
 * instead as a card: the first cell is the summary, because that is the name somebody is scanning
 * for, and every other column is a labelled line underneath. Collapsed to begin with, so the
 * screen is a list of names rather than a wall of everything.
 *
 * **The same cells, arranged twice — not two lists to keep in step.** The rows a page writes are
 * read apart here: `head`'s `<th>`s give the labels, each `<tr>`'s `<td>`s give the values, and
 * the card is built from those. A page adds a column in one place and both layouts have it.
 *
 * A row that spans the table — the empty states — is drawn as it is, with no card around it.
 */
/**
 * The rows, with any `<Fragment>` opened up.
 *
 * `Children.toArray` treats a fragment as **one child**, which is right for the table — a fragment
 * of `<tr>`s in a `<tbody>` renders exactly as written — and wrong for the card collapse below it.
 * There, the fragment was taken for a row and its `<tr>`s for cells, so a `<td>` was rendered
 * straight into a `<div>`: invalid HTML, a React nesting warning, and a card showing the wrong
 * thing. A table that groups rows — a price list with a heading row and its members — is the
 * ordinary reason to reach for a fragment, so this is not an exotic case.
 *
 * Recursive, because a fragment can hold a fragment. Anything that is not an element is dropped:
 * `false` and `null` are how a conditional row is written and are not rows.
 */
function flattenRows(children: ReactNode): ReactNode[] {
  return Children.toArray(children).flatMap((child) => {
    if (!isValidElement(child)) return [];
    if (child.type === Fragment) return flattenRows((child.props as { children?: ReactNode }).children);
    return [child];
  });
}

export function Table({ head, children }: { head: ReactNode; children: ReactNode }) {
  const labels = headingLabels(head);
  const rows = flattenRows(children);

  return (
    <>
      {/* The table, from `sm` up. */}
      <div className="hidden overflow-x-auto overflow-y-hidden sm:block">
        <table className="table">
          <thead>{head}</thead>
          <tbody>{children}</tbody>
        </table>
      </div>

      {/* The same rows, as cards. */}
      <div className="divide-y divide-ink-100 sm:hidden">
        {rows.map((row, index) => (
          <RowCard key={(row as { key?: string }).key ?? index} row={row} labels={labels} />
        ))}
      </div>
    </>
  );
}

/** The words above each column, in order. A column with only a control in it has none. */
function headingLabels(head: ReactNode): string[] {
  const row = Children.toArray(head)[0];
  if (!isValidElement(row)) return [];
  return Children.toArray((row.props as { children?: ReactNode }).children).map((cell) =>
    isValidElement(cell) ? plainText((cell.props as { children?: ReactNode }).children) : "",
  );
}

/** Whatever text a node would show, ignoring the markup it is wrapped in. */
function plainText(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  // A space between siblings: a cell holding two stacked lines — "Flat 3B" over "Palm Court" —
  // ran them together into "Flat 3BPalm Court" when they were simply concatenated.
  if (Array.isArray(node)) return node.map(plainText).join(" ");
  if (isValidElement(node)) return plainText((node.props as { children?: ReactNode }).children);
  return "";
}

/**
 * One record, on a phone.
 *
 * Shut, it is the first cell and nothing else — a list of names you can run an eye down. Open, it
 * is every other column as a labelled line. The chevron turns rather than the card jumping, so it
 * is obvious which one moved.
 */
function RowCard({ row, labels }: { row: ReactNode; labels: string[] }) {
  const [open, setOpen] = useState(false);
  if (!isValidElement(row)) return null;

  const cells = Children.toArray((row.props as { children?: ReactNode }).children).filter(isValidElement);
  if (!cells.length) return null;

  // An empty state spans the whole table. It is a sentence, not a record.
  const spans = cells.length === 1 && Boolean((cells[0].props as { colSpan?: number }).colSpan);
  if (spans) return <div className="px-4 py-6 text-center text-sm text-ink-500">{(cells[0].props as { children?: ReactNode }).children}</div>;

  /**
   * Which cell is the record.
   *
   * Not always the first one: an invoice row opens with a tick box and a maintenance row ends with
   * two buttons, and neither is what somebody is scanning for. The summary is the first cell whose
   * column has a heading — a column with no heading holds a control, so it keeps its place beside
   * the name rather than becoming a labelled line saying nothing.
   */
  const summaryIndex = Math.max(labels.findIndex((label, index) => label && index < cells.length), 0);
  const summary = cells[summaryIndex];
  const before = cells.filter((_, index) => index < summaryIndex);
  const after = cells.filter((_, index) => index > summaryIndex && !labels[index]);
  const detail = cells
    .map((cell, index) => ({ label: labels[index] ?? "", value: (cell.props as { children?: ReactNode }).children, index }))
    .filter((entry) => entry.index !== summaryIndex && entry.label)
    .filter((entry) => entry.value !== null && entry.value !== undefined && entry.value !== false);

  /**
   * What a shut card says besides the name.
   *
   * A list of nothing but names makes you open every one to find the row you meant. So the two
   * shortest few words from the first columns, and the last column — which in almost every list
   * here is the money — ride along underneath. Long values are left out rather than truncated: a
   * clipped address tells you less than no address, and it costs the line its shape.
   *
   * It is dropped the moment the card opens, because everything in it is then a line below.
   */
  const preview = (() => {
    const short = detail.filter((entry) => {
      const text = plainText(entry.value).replace(/\s+/g, " ").trim();
      // An em dash is what a column shows when it has nothing to say, and it is not worth a slot.
      return text && text !== "—" && text !== "-" && text.length <= 22;
    });
    const last = short[short.length - 1];
    const lead = short.slice(0, 2).filter((entry) => entry !== last);
    return [...lead, ...(last ? [last] : [])].slice(0, 3).map((entry) => {
      const text = plainText(entry.value).replace(/\s+/g, " ").trim();
      // A bare "1" says nothing on its own, so a count carries its column's word with it. An
      // amount does not: it is already obvious what ₦900,000.00 is.
      return /^\d{1,4}$/.test(text) ? `${text} ${entry.label.toLowerCase()}` : text;
    });
  })();

  const toggle = () => setOpen((current) => !current);

  /**
   * A tap on the card opens or shuts it. A tap on a control does what the control does.
   *
   * The whole head used to be a toggle except for the name column, which swallowed its clicks so
   * the link inside could still work — and the name column is most of the row, so tapping the row
   * did nothing. Now the click is let through and judged by what it landed on: an anchor, a button
   * or a field keeps its own behaviour, and everything else — the blank space, the summary line,
   * the chevron — is a tap on the card.
   */
  const headClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest("a, button, input, select, textarea, label")) return;
    toggle();
  };

  return (
    <div className="px-4 py-1">
      {/*
        The whole head opens it.
        A chevron alone is a 36px target on a row that is 350px wide, and everybody taps the row.
        The link inside keeps its own job — a tap on the name still opens the record — which is why
        the summary cell swallows the click rather than passing it up.
      */}
      <div
        role={detail.length ? "button" : undefined}
        tabIndex={detail.length ? 0 : undefined}
        aria-expanded={detail.length ? open : undefined}
        onClick={detail.length ? headClick : undefined}
        onKeyDown={
          detail.length
            ? (event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                toggle();
              }
            : undefined
        }
        className={`flex items-center gap-2 ${detail.length ? "cursor-pointer" : ""}`}
      >
        {/* Leading controls — a tick box — stay where they are on the desktop row: first. */}
        {before.map((cell, index) => (
          <div key={`before-${index}`} className="shrink-0">
            {(cell.props as { children?: ReactNode }).children}
          </div>
        ))}

        <div className="min-w-0 flex-1 py-2.5">
          <div className="truncate text-sm font-medium text-ink-900">
            {(summary.props as { children?: ReactNode }).children}
          </div>
          {!open && preview.length ? (
            <p className="mt-0.5 truncate text-xs text-ink-500">{preview.join(" · ")}</p>
          ) : null}
        </div>

        {/* Trailing controls — the row's own buttons. */}
        {/* Capped: a status dropdown is a control, not the record, and at full width it took
            half the row from the reference somebody is actually looking for. */}
        {after.map((cell, index) => (
          <div key={`after-${index}`} className="max-w-[42%] shrink-0">
            {(cell.props as { children?: ReactNode }).children}
          </div>
        ))}

        {detail.length ? (
          <span
            aria-hidden
            className="-mr-2 flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-control)] text-ink-400"
          >
            <IconChevronDown size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </span>
        ) : null}
      </div>

      {open ? (
        <dl className="mb-1 space-y-1.5 pb-2">
          {detail.map((entry, index) => (
            <div key={index} className="grid grid-cols-[minmax(6.5rem,40%)_minmax(0,1fr)] items-baseline gap-3">
              <dt className="text-xs font-semibold uppercase tracking-wide text-ink-500">{entry.label}</dt>
              <dd className="min-w-0 text-sm text-ink-800">{entry.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </div>
  );
}

export function Alert({ children, tone = "info", title }: { children?: ReactNode; tone?: Tone; title?: ReactNode }) {
  return (
    <div className={`rounded-[var(--radius-surface)] border px-4 py-3 text-sm ${TONE_CLASSES[tone]}`}>
      {title ? <p className="font-semibold">{title}</p> : null}
      {children ? <div className={title ? "mt-1 leading-relaxed" : "leading-relaxed"}>{children}</div> : null}
    </div>
  );
}

/** A definition row — the label/value pattern used all over the detail screens. */
export function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-ink-100 py-2.5 last:border-0">
      <dt className="eyebrow shrink-0">{label}</dt>
      <dd className="tabular text-right text-sm font-medium text-ink-900">{children}</dd>
    </div>
  );
}

export function Tabs({ tabs, current }: { tabs: Array<{ to: string; label: string; count?: number }>; current: string }) {
  return (
    /* `overflow-x-auto` alone is not what it looks like: when one axis is `auto` and the other
       `visible`, the browser promotes *both* to `auto` — so a strip one pixel taller than its
       content (which `-mb-px` and the padding make it) grows a vertical scrollbar nobody asked
       for. Pinning the vertical axis is the fix. */
    <div className="mb-5 flex gap-6 overflow-x-auto overflow-y-hidden border-b border-ink-200">
      {tabs.map((tab) => {
        const active = tab.to === current;
        return (
          <Link
            key={tab.to}
            to={tab.to}
            /* Which one you are on, said and not only drawn. It was carried by a colour and a
               weight, which is nothing at all to a screen reader — and it is the one thing about a
               tab strip somebody needs to know before they choose. */
            aria-current={active ? "page" : undefined}
            className={`-mb-px flex items-center gap-2 whitespace-nowrap border-b-2 pb-3 pt-1 text-sm transition-colors ${
              active ? "border-brand-600 font-semibold text-ink-900" : "border-transparent text-ink-500 hover:text-ink-800"
            }`}
          >
            {tab.label}
            {typeof tab.count === "number" ? (
              <span
                className={`tabular rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${
                  active ? "bg-brand-50 text-brand-700" : "bg-ink-100 text-ink-500"
                }`}
              >
                {tab.count}
              </span>
            ) : null}
          </Link>
        );
      })}
    </div>
  );
}

/**
 * Waiting.
 *
 * The mark's own street, lit one building at a time — `BrandSpinner`. It replaced a grey ring,
 * which is what every application in the world uses while it thinks and therefore says nothing
 * about this one. The whole loop is still the logo at every moment of it, so a slow screen looks
 * like the product working rather than like a browser hanging.
 *
 * `size` is the thing to reach for: `sm` beside a control, `md` in a panel, `lg` on an empty
 * screen. This wrapper is the panel case, because that is what a hundred call sites already ask
 * for by writing `<Spinner />` and none of them should have to change.
 */
export function Spinner({ label = "Loading…", size = "md" }: { label?: string; size?: "sm" | "md" | "lg" }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-4 py-16 text-sm text-ink-500" role="status">
      <BrandSpinner size={size} label={label} />
      {/* The label is drawn as well as announced: somebody watching a slow screen wants to know
          what it is waiting for, and `aria-label` alone tells only the screen reader. */}
      <span aria-hidden>{label}</span>
    </div>
  );
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const message = error instanceof Error ? error.message : "Something went wrong.";
  return (
    <div className="px-6 py-14 text-center">
      <p className="text-sm font-semibold text-danger-700">{message}</p>
      {retry ? (
        <div className="mt-3">
          <Button variant="secondary" onClick={retry}>
            Try again
          </Button>
        </div>
      ) : null}
    </div>
  );
}
